import type { Box3 } from 'three'
import type { TilesViewSnapshot } from '@/plugins/tiles/contracts'
import type { RuntimeTileNode } from '@/plugins/tiles/hierarchy/ExplicitHierarchyProvider'
import {
  computeTilesSSE,
  distanceToPreparedTilesBounds,
  getMaximumStretch,
  intersectsPreparedTilesBounds,
  type OrientedBounds,
  prepareTilesBounds,
} from '@/plugins/tiles/spatial/tilesSpatial'
import type { TilesRuntimeOptions } from '@/plugins/tiles/types'

export interface TileSelectionRequest {
  readonly node: RuntimeTileNode
  readonly kind: 'classify' | 'hierarchy' | 'content'
  readonly priority: number
  readonly purpose?: 'display' | 'collision'
}

export interface TileSelectionResult {
  readonly requests: readonly TileSelectionRequest[]
  readonly desired: readonly RuntimeTileNode[]
  readonly displayed: readonly RuntimeTileNode[]
  readonly hasCoverage: boolean
  readonly sourceLimited: boolean
  readonly hasUnknownHierarchy: boolean
  readonly qualityLimited: boolean
  readonly budgetLimited: boolean
}

export interface TileSelectionPolicy {
  readonly refinementStops?: ReadonlySet<string>
  readonly hysteresisRatio?: number
}

type BranchSelection = {
  desired: RuntimeTileNode[]
  displayed: RuntimeTileNode[]
  covered: boolean
  targetMet: boolean
}

/** 同步单 view 选择；ready 快照必须表示已完成注册/GPU 准备，不能只表示下载完成。 */
export class TileSelector {
  select(
    root: RuntimeTileNode,
    view: TilesViewSnapshot,
    options: TilesRuntimeOptions,
    isContentReady: (nodeId: string) => boolean,
    isContentFailed: (nodeId: string) => boolean = () => false,
    previousDisplayed: readonly RuntimeTileNode[] = [],
    policy: TileSelectionPolicy = {}
  ): TileSelectionResult {
    const hysteresisRatio = policy.hysteresisRatio ?? 0
    if (!Number.isFinite(hysteresisRatio) || hysteresisRatio < 0 || hysteresisRatio > 1)
      throw new RangeError('Tiles 细化滞回比例必须在0到1之间')
    const requests = new Map<string, TileSelectionRequest>()
    let discoveryCount = 0
    let sourceLimited = false
    let hasUnknownHierarchy = false
    let budgetLimited = false
    const hostStretch = getMaximumStretch(view.placement)
    const request = (
      node: RuntimeTileNode,
      kind: TileSelectionRequest['kind'],
      priority: number
    ): void => {
      const key = `${kind}:${node.id}`
      if (requests.has(key)) return
      if (kind !== 'content') {
        if (discoveryCount >= options.maxConcurrentRequests) return
        discoveryCount++
      }
      requests.set(key, { node, kind, priority })
    }
    const discover = (node: RuntimeTileNode, priority: number): void => {
      if (!node.description.content || node.hierarchyState === 'ready') return
      hasUnknownHierarchy = true
      // pending 仍是当前需求，运行时按任务键去重并保活；failed 由重试策略显式复位。
      if (node.hierarchyState === 'failed') return
      if (node.contentKind === 'unknown') request(node, 'classify', priority)
      else if (node.contentKind === 'tileset') request(node, 'hierarchy', priority)
    }
    // 仅在本次同步选择内持有，不跨 view、帧或预算重选复用。
    const spatial = new Map<
      RuntimeTileNode,
      { visible?: boolean; shape?: OrientedBounds; traversal?: Box3 }
    >()
    const entry = (node: RuntimeTileNode) => {
      let value = spatial.get(node)
      if (!value) {
        value = {}
        spatial.set(node, value)
      }
      return value
    }
    const shape = (node: RuntimeTileNode): OrientedBounds =>
      (entry(node).shape ??= prepareTilesBounds(
        node.description.boundingVolume,
        node.tileMatrix,
        node.sourceToRecord,
        view.placement
      ))
    const traversal = (node: RuntimeTileNode): Box3 =>
      (entry(node).traversal ??= node.traversalBounds.clone().applyMatrix4(view.placement))
    const visible = (node: RuntimeTileNode): boolean =>
      (entry(node).visible ??=
        options.boundsPolicy === 'conservative'
          ? view.frustum.intersectsBox(traversal(node))
          : intersectsPreparedTilesBounds(shape(node), view.frustum))

    // 仅从仍有效的上次提交沿 parent 建立路径，不扫描整棵树保存历史。
    const previousAncestors = new Set<string>()
    for (const node of previousDisplayed) {
      if (!isContentReady(node.id) || !visible(node)) continue
      let parent = node.parent
      while (parent && !previousAncestors.has(parent.id)) {
        previousAncestors.add(parent.id)
        parent = parent.parent
      }
    }

    const coverage = new Map<string, boolean>()
    // 当前可提交的粗覆盖：空结构和已确定失败的REPLACE内容透明穿过。
    // 只读ready快照，避免提前启动深层请求占用直接孩子安全替换所需的峰值预算。
    const hasCoarseCoverage = (node: RuntimeTileNode): boolean => {
      const cached = coverage.get(node.id)
      if (cached !== undefined) return cached
      let covered: boolean
      const failed = isContentFailed(node.id)
      if (!visible(node) || (node.contentKind === 'mesh' && isContentReady(node.id))) {
        covered = true
      } else if (
        node.description.content &&
        !failed &&
        (node.contentKind === 'unknown' ||
          (node.contentKind === 'tileset' && node.hierarchyState !== 'ready'))
      ) {
        covered = false
      } else if (node.children.length === 0) {
        covered = !node.description.content
      } else if (
        node.description.refine === 'ADD' &&
        (node.contentKind === 'mesh' || node.contentKind === 'unsupported')
      ) {
        covered = false
      } else {
        covered = node.children.every(hasCoarseCoverage)
      }
      coverage.set(node.id, covered)
      return covered
    }

    const visit = (
      node: RuntimeTileNode,
      startNewRefinement = true,
      coverageDepth = 0
    ): BranchSelection => {
      if (!visible(node)) return { desired: [], displayed: [], covered: true, targetMet: true }
      discover(node, 1e12 - coverageDepth * 1e6 + 2e5)
      let distance = distanceToPreparedTilesBounds(shape(node), view.cameraPosition)
      if (options.boundsPolicy === 'conservative') {
        distance = Math.min(distance, traversal(node).distanceToPoint(view.cameraPosition))
      }
      const sse = computeTilesSSE(
        node.effectiveError * hostStretch,
        distance,
        view.projectionMatrix,
        view.cssHeight,
        view.perspective
      )
      const hasMesh = node.contentKind === 'mesh'
      const ready = hasMesh && isContentReady(node.id)
      const failed = isContentFailed(node.id)
      const ownPending =
        !!node.description.content &&
        !failed &&
        (node.contentKind === 'unknown' ||
          (node.contentKind === 'tileset' && node.hierarchyState !== 'ready'))
      const hasPreviousDescendants = previousAncestors.has(node.id)
      const threshold =
        options.maxScreenSpaceError * (hasPreviousDescendants ? 1 - hysteresisRatio : 1)
      const wantsRefine = node.children.length > 0 && (sse > threshold || !hasMesh || failed)
      if (ready && policy.refinementStops?.has(node.id)) {
        if (sse > options.maxScreenSpaceError) {
          if (node.children.length > 0) budgetLimited = true
          else sourceLimited = true
        }
        // 预算停止优先于旧cut与滞回，只提交已经可绘制的粗层，绝不叠画其后代。
        return {
          desired: [node],
          displayed: [node],
          covered: true,
          targetMet: sse <= options.maxScreenSpaceError && !ownPending,
        }
      }
      // 新请求的细化额度与读取已提交细节分开；回粗必须等粗层自身 ready。
      const retainPrevious = hasPreviousDescendants && (wantsRefine || !ready)
      const refine = (wantsRefine && (startNewRefinement || !hasMesh || failed)) || retainPrevious
      // 先建立较粗覆盖；子节点误差可以回升，但不能挤掉尚缺失的父层回退。
      // 覆盖层是硬优先级：空结构不惩罚其内容，深层不能抢直接孩子的覆盖额度。
      // 同层用有界SSE分数与距离排序；零误差叶节点仍优先近处。
      const priority =
        1e12 - coverageDepth * 1e6 + (Math.atan(sse) / (Math.PI / 2)) * 1e5 + 100 / (1 + distance)
      if (hasMesh && !ready && !failed) request(node, 'content', priority)

      if (!refine) {
        const empty = !node.description.content && node.children.length === 0
        const limitedLeaf =
          hasMesh && node.children.length === 0 && sse > options.maxScreenSpaceError
        sourceLimited ||= limitedLeaf
        return {
          desired: hasMesh ? [node] : [],
          displayed: ready ? [node] : [],
          covered: ready || empty,
          targetMet: (ready || empty) && !ownPending && !limitedLeaf && !wantsRefine,
        }
      }

      const advanceFrontier = startNewRefinement && node.children.every(hasCoarseCoverage)
      const branches = node.children.map((child) =>
        visit(child, advanceFrontier, coverageDepth + (hasMesh ? 1 : 0))
      )
      const desiredChildren = branches.flatMap((branch) => branch.desired)
      const displayedChildren = branches.flatMap((branch) => branch.displayed)
      const childrenCovered = branches.every((branch) => branch.covered)
      const childrenTargetMet = branches.every((branch) => branch.targetMet)
      if (node.description.refine === 'ADD') {
        return {
          desired: hasMesh ? [node, ...desiredChildren] : desiredChildren,
          displayed: ready ? [node, ...displayedChildren] : displayedChildren,
          covered:
            (!hasMesh || ready) &&
            childrenCovered &&
            !ownPending &&
            node.contentKind !== 'unsupported',
          targetMet:
            (!hasMesh || ready) &&
            childrenTargetMet &&
            !ownPending &&
            node.contentKind !== 'unsupported',
        }
      }
      if (childrenCovered && !ownPending) {
        if (wantsRefine) requests.delete(`content:${node.id}`)
        return {
          desired: desiredChildren,
          displayed: displayedChildren,
          covered: true,
          targetMet: childrenTargetMet,
        }
      }
      if (retainPrevious && displayedChildren.length) {
        // 新可见兄弟缺口不撤掉仍可绘制的旧 cut，也不叠画其粗祖先。
        // 缺口如实报告，保留既有新请求 frontier，等待所需兄弟就绪。
        if (wantsRefine) requests.delete(`content:${node.id}`)
        return {
          desired: desiredChildren,
          displayed: displayedChildren,
          covered: false,
          targetMet: false,
        }
      }
      return {
        desired: desiredChildren,
        displayed: ready ? [node] : displayedChildren,
        covered: ready,
        targetMet: false,
      }
    }

    const selected = visit(root)
    if (options.boundsPolicy === 'conservative' && root.hasUnknownDescendants) {
      hasUnknownHierarchy = true
      // 仅沿尚未知的路径发现外部层次，额度是本轮请求上限；已知 mesh 不会被离屏下载。
      const stack = [root]
      while (stack.length && discoveryCount < options.maxConcurrentRequests) {
        const node = stack.pop()!
        discover(node, 1 - node.depth)
        for (let i = node.children.length - 1; i >= 0; i--) {
          if (node.children[i].hasUnknownDescendants) stack.push(node.children[i])
        }
      }
    }
    return {
      requests: [...requests.values()].sort((a, b) => b.priority - a.priority),
      desired: selected.desired,
      displayed: selected.displayed,
      hasCoverage: selected.covered,
      sourceLimited,
      hasUnknownHierarchy,
      qualityLimited: !selected.targetMet || sourceLimited || hasUnknownHierarchy,
      budgetLimited,
    }
  }
}
